import { useCallback, useRef, useState } from "react";

/** D1/DO scope fetch identity — loading until this key has resolved. */
export function scopeLoadKey(orgSlug?: string, projectSlug?: string): string {
  return `${orgSlug ?? ""}/${projectSlug ?? ""}`;
}

export function isScopeLoading(
  orgSlug: string | undefined,
  loadedKey: string | null,
  currentKey: string,
): boolean {
  return Boolean(orgSlug) && loadedKey !== currentKey;
}

/** Route-keyed pending flag: true on the same render as `key` changes; ignores stale in-flight work. */
export function useKeyedLoad(key: string): {
  loading: boolean;
  start: () => () => boolean;
  finish: (isCurrent: () => boolean) => void;
} {
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const genRef = useRef(0);
  const loading = loadedKey !== key;

  const start = useCallback(() => {
    const gen = ++genRef.current;
    return () => genRef.current === gen;
  }, []);

  const finish = useCallback(
    (isCurrent: () => boolean) => {
      if (isCurrent()) setLoadedKey(key);
    },
    [key],
  );

  return { loading, start, finish };
}
